/**
 * API client for the facilitator session-release flow.
 *
 * A facilitator (e.g. a course instructor) holds the group-scoped :facilitator
 * role on a Group containing this story, granting them a single capability:
 * releasing sessions (flipping thread `active`) to pace a live cohort. They are
 * NOT admins — the backend authorizes via :release_session, and the threads
 * index lets them see locked (not-yet-released) sessions for their own stories
 * so they know what to release next.
 *
 * Mirrors the envelope-unwrap pattern used by the other services.
 * @module facilitatorService
 */

import apiClient from '../lib/apiClient'

export const facilitatorService = {
  /**
   * Read the group-scoped facilitator surface for one group: its stories,
   * members (read-only, with each member's team), and teams (started or not).
   * Authorized for a group facilitator or admin.
   * @param {number|string} groupId
   * @returns {Promise<{id, name, stories, members, teams}>}
   */
  async getGroup(groupId) {
    const response = await apiClient.get(`/api/facilitator_groups/${groupId}`)
    return response.data?.data || response.data
  },

  /**
   * List a story's sessions (threads) in order. As a facilitator of this story
   * the backend returns BOTH released and locked threads (a plain player only
   * sees released ones).
   * @param {string} storyId
   * @returns {Promise<Array>} thread objects (have position, active, name)
   */
  async getSessions(storyId) {
    const response = await apiClient.get(`/api/stories/${storyId}/threads`, {
      params: { limit: 100 },
    })
    const data = response.data?.data || response.data
    return Array.isArray(data) ? data : []
  },

  /**
   * Cumulatively release sessions: threads 1..N (by position) become active,
   * the rest locked. Strict cumulative — a lower N steps back. Returns the
   * authoritative thread state so the caller need not refetch.
   * @param {string} storyId
   * @param {number} through - release through session N
   * @returns {Promise<{story_id: string, released_through: number, total_sessions: number, threads: Array}>}
   */
  async releaseSession(storyId, through) {
    const response = await apiClient.post(
      `/api/stories/${storyId}/release_session`,
      { through },
    )
    return response.data?.data || response.data
  },

  /**
   * Set the GROUP-WIDE default role caps. This is the primary control: every
   * team in the group inherits these unless it has its own per-team override.
   * `roleCaps` is a map of roleKey -> integer (1 = unique default, >1 = up to N,
   * 0 = unlimited). Group facilitator or admin.
   * @param {number|string} groupId
   * @param {Record<string, number>} roleCaps
   * @returns {Promise<{group_id, role_caps}>}
   */
  async setGroupRoleCaps(groupId, roleCaps) {
    const response = await apiClient.patch(
      `/api/facilitator_groups/${groupId}/role_caps`,
      { role_caps: roleCaps },
    )
    return response.data?.data || response.data
  },
}
